// src/merch/DesignViewer.jsx
//
// /view/:id — read-only 3D view of a saved design. For printers (every
// order links a frozen snapshot), and for customers to share. Shows the
// model exactly as designed, every zone, with product/size/colour/hand.

import { StoreLogo } from "./StoreBrand.jsx";
import React, { useEffect, useMemo, useState } from "react";
import { buildFlattenedUV } from "../engine";
import { getProduct, getSize, getColour, getSides } from "./products";
import { loadDesignData } from "./designs";
import ProductViewer from "./ProductViewer.jsx";

export default function DesignViewer({ designId, homeHref }) {
  const [state, setState] = useState(null); // { meta, data }
  const [error, setError] = useState(null);
  const [composites, setComposites] = useState({});
  const [active, setActive] = useState(null);
  const [viewNonce, setViewNonce] = useState(0);

  useEffect(() => {
    loadDesignData(designId).then(setState).catch((e) => setError(e.message || "Couldn't open that design."));
  }, [designId]);

  const info = useMemo(() => {
    if (!state) return null;
    const d = state.data;
    const product = getProduct(d.productId);
    const size = getSize(product, d.sizeId);
    const colour = getColour(product, d.colourId);
    const zones = getSides(product, size, d.hand);
    return { d, product, size, colour, zones };
  }, [state]);

  // one composite per zone, straight from the saved layers
  useEffect(() => {
    if (!info) return;
    let alive = true;
    (async () => {
      const next = {};
      for (const z of info.zones) {
        const layers = info.d.sides?.[z.id] || [];
        next[z.id] = layers.length ? await buildFlattenedUV(layers, { size: 1024, edgeClip: 0.05 }) : null;
      }
      if (alive) {
        setComposites(next);
        setActive((a) => a || (info.zones.find((z) => next[z.id]) || info.zones[0]).id);
      }
    })();
    return () => { alive = false; };
  }, [info]);

  const parts = info ? [...new Set(info.zones.map((z) => z.part || "main"))] : [];

  return (
    <div className="ma-app ma-viewer-page">
      <header className="ma-header">
        <StoreLogo link={homeHref} />
        <div className="ma-sub">3D view</div>
      </header>
      <main className="ma-view-main">
        {error && <div className="ma-note error">{error}</div>}
        {!info && !error && <p className="ma-muted">Loading design…</p>}
        {info && (
          <>
            <div className="ma-view-stage">
              <ProductViewer
                product={info.product}
                colour={info.colour}
                zones={info.zones}
                composites={composites}
                activeZone={active || info.zones[0].id}
                viewKey={viewNonce}
              />
            </div>
            <aside className="ma-panel ma-view-info">
              <h2>{info.product.name}</h2>
              <div className="ma-view-facts">
                <div><span>Size</span><b>{info.size.label}</b></div>
                <div><span>Colour</span><b><i className="ma-order-dot" style={{ background: info.colour.hex }} />{info.colour.label}</b></div>
                {info.product.handed && <div><span>Shot</span><b>{info.d.hand === "left" ? "Left" : "Right"}</b></div>}
                <div><span>Design</span><b>#{designId.slice(0, 8).toUpperCase()}{state.meta.frozen ? " · order copy" : ""}</b></div>
              </div>
              <h3>Look at</h3>
              <div className="ma-zones">
                {parts.map((part) => (
                  <div className="ma-zone-row" key={part}>
                    {parts.length > 1 && <span>{part === "main" ? "" : part[0].toUpperCase() + part.slice(1)}</span>}
                    <div className="ma-seg zones">
                      {info.zones.filter((z) => (z.part || "main") === part).map((z) => (
                        <button
                          key={z.id}
                          aria-pressed={z.id === active}
                          onClick={() => { setActive(z.id); setViewNonce((n) => n + 1); }}
                        >
                          {z.face ? z.face[0].toUpperCase() + z.face.slice(1) : z.label}
                          {composites[z.id] ? <i className="ma-zone-dot" /> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="ma-muted">Drag to turn · right-drag or two fingers to move · scroll or pinch to zoom.</p>
              {!state.meta.frozen && <a className="ma-btn" href={homeHref}>Make your own</a>}
            </aside>
          </>
        )}
      </main>
    </div>
  );
}
